import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateCategoryDTO,
  UpdateCategoryDTO,
} from "@/modules/form-center/domain/contracts/categories";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { categoriesApi } from "./categories.client";
import { formsKeys } from "./queries";

export const categoriesKeys = {
  all: ["categories"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["categories", "list", q] as const,
  detail: (id: string) => ["categories", "detail", id] as const,
};

export function useCategoriesQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: categoriesKeys.list(q),
    queryFn: () => categoriesApi.list(q),
  });
}

export function useCategoryQuery(id: string) {
  return useQuery({
    queryKey: categoriesKeys.detail(id),
    queryFn: () => categoriesApi.get(id),
    enabled: !!id,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCategoryDTO) => categoriesApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງຫມວດຫມູ່ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: categoriesKeys.all });
    },
    onError: () => toast.error("ສ້າງຫມວດຫມູ່ລົ້ມເຫຼວ"),
  });
}

export function useUpdateCategory(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCategoryDTO) => categoriesApi.update(id, input),
    onSuccess: () => {
      toast.success("ແກ້ໄຂຫມວດຫມູ່ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: categoriesKeys.detail(id) });
      qc.invalidateQueries({ queryKey: categoriesKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂຫມວດຫມູ່ລົ້ມເຫຼວ"),
  });
}

export function useUpdateCategoryAny() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateCategoryDTO & { id: string }) =>
      categoriesApi.update(id, input),
    onSuccess: (_, variables) => {
      toast.success("ແກ້ໄຂຫມວດຫມູ່ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: categoriesKeys.detail(variables.id) });
      qc.invalidateQueries({ queryKey: categoriesKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂຫມວດຫມູ່ລົ້ມເຫຼວ"),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => categoriesApi.remove(id),
    onSuccess: () => {
      toast.success("ລຶບຫມວດຫມູ່ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: categoriesKeys.all });
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
    onError: () => toast.error("ລຶບຫມວດຫມູ່ລົ້ມເຫຼວ"),
  });
}
